import { memo, useState, useMemo } from 'react';
import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import type { ResolverEntry } from '../types';

interface ResolverPickerProps {
  resolvers: ResolverEntry[];
  value: string | null;
  onChange: (resolverId: string) => void;
  disabled?: boolean;
  loading?: boolean;
}

const ResolverPicker = memo<ResolverPickerProps>(({
  resolvers,
  value,
  onChange,
  disabled = false,
  loading = false,
}) => {
  const [inputValue, setInputValue] = useState('');

  const filtered = useMemo(() => {
    if (!inputValue.trim()) return resolvers;
    const q = inputValue.toLowerCase();
    return resolvers.filter(
      (r) => r.id.toLowerCase().includes(q) || r.description.toLowerCase().includes(q),
    );
  }, [resolvers, inputValue]);

  const selected = resolvers.find((r) => r.id === value) ?? null;

  return (
    <Autocomplete
      options={filtered}
      getOptionLabel={(r) => r.id}
      value={selected}
      onChange={(_, r) => r && onChange(r.id)}
      inputValue={inputValue}
      onInputChange={(_, v) => setInputValue(v)}
      loading={loading}
      disabled={disabled}
      filterOptions={(x) => x}
      isOptionEqualToValue={(a, b) => a.id === b.id}
      renderInput={(params) => (
        <TextField
          {...params}
          size="small"
          label="Resolver"
          placeholder="Search by id or description…"
          sx={{ '& input': { fontFamily: 'monospace', fontSize: '0.8rem' } }}
        />
      )}
      renderOption={(props, r) => (
        <Box component="li" {...props} key={r.id}>
          <Box>
            <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 600, fontSize: '0.8rem' }}>
              {r.id}
            </Typography>
            {r.description && (
              <Typography variant="caption" color="text.secondary">{r.description}</Typography>
            )}
            {r.example && (
              <Typography variant="caption" sx={{ display: 'block', fontFamily: 'monospace', color: 'text.disabled', fontSize: '0.7rem' }}>
                e.g. {r.example}
              </Typography>
            )}
          </Box>
        </Box>
      )}
      noOptionsText="No resolvers found"
      sx={{ minWidth: 280 }}
    />
  );
});

ResolverPicker.displayName = 'ResolverPicker';
export default ResolverPicker;
